/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  AlertColor,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';

import {
  DebounceTime,
  DEFAULT_FROM_DATE,
  DEFAULT_PAGE_NUMBER,
  Paths,
  SnackbarSeverity,
  SortOrderType,
  timeoutValue,
  UserType,
} from '../../../../Shared/Constants/App';
import AsyncStorageKeys from '../../../../Shared/Constants/StorageKeys';

import CustomDropDownAutoComplete from '../../../../Shared/Components/Common/CustomDropDown/CustomDropdownAutoComplete';
import PageTitle from '../../../../Shared/Components/Common/PageTitle/PageTitle';
import SnackbarMessage from '../../../../Shared/Components/Common/SnackbarMessage/SnackbarMessage';

import {
  getAuthData,
  getDefaultServiceValueForScheduleCreation,
  getStoredCustomerDetails,
  NUMBER_OF_ITEMS_PER_PAGE,
} from '../../../../Shared/Utilities/utils';

import { JobType } from '../../../../API';
import { IServiceItem } from '../../Models/AddEditOneTimeJob.Model';

import MasterDataApiService from '../../../../Shared/Services/MasterDataService';
import JobCreateApiService, { ICreateServiceRequest } from '../../Services/JobCreateService';
import JobViewApiService from '../../Services/JobViewService';
import ScheduleService from '../../../Vendor/Components/AllJobs/CreateSchedule/ScheduleService';

import PhotosAndVideos from '../AddEditOneTimeJob/PhotosAndVideos';

import theme from '../../../../Shared/Themes/theme';
import { CustomerContainer } from '../../../../Shared/Components/Layout/styles';
import { BottomBarContainer, buttonStyles, JobNameContainer } from '../AddEditOneTimeJob/AddEditOneTimeJob.Style';
import { Options } from '../../../../Shared/Components/Common/Dropdown/Dropdown.styles';
import { BoxShadow } from '../../../../Shared/Constants/Style.Constants';
import { Icons } from '../../../../Shared/Constants/Icons';

const attachments = localStorage.getItem(AsyncStorageKeys.serviceRequestAttachments);

const getDefaultJobAttachments = () => {
  return attachments ? JSON.parse(attachments) : [];
};

const ServiceRequest: React.FC = () => {
  const { t } = useTranslation(['serviceRequests', 'oneTimeJob']);
  const [isJobsLoading, setIsJobsLoading] = useState(false);
  const [jobsOptions, setJobsOptions] = useState<{ label: string; value: string; facilityId: string }[]>([]);
  const [selectedJob, setSelectedJob] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE_NUMBER);
  const [totalCount, setTotalCount] = useState(NUMBER_OF_ITEMS_PER_PAGE);
  const [searchString, setSearchString] = useState<string>('');
  const [service, setService] = useState<IServiceItem[]>(getDefaultServiceValueForScheduleCreation());
  const [serviceTasks, setServiceTasks] = useState<any>({});
  const [locationDescription, setLocationDescription] = useState<string>();
  const [issueDescription, setIssueDescription] = useState<string>();
  const [additionalNotes, setAdditionalNotes] = useState<string>();
  const [jobAttachments, setJobAttachments] = useState<any[]>(getDefaultJobAttachments());
  const [isCreationLoading, setIsCreationLoading] = useState(false);
  const [selectedFacilityId, setSelectedFacilityId] = useState<string>('');
  const [multiCustomerOrgIds, setMultiCustomerOrgIds] = useState<string[] | null>(null);

  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [isCustomerOpen, setCustomerOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>(SnackbarSeverity.success);

  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigate = useNavigate();

  const { textField, createJobButton, buttonText, divider, saveExitButton, headingText } = buttonStyles;
  const { customerId = '', userId } = getStoredCustomerDetails() || {};

  const { role } = getAuthData();
  const isVelocitiVendorORUnifi = role === UserType.Vendor && !!multiCustomerOrgIds?.includes(customerId as string);
  // The customer field is only rendered for multi-customer vendors, so only they can be waiting on it.
  const isAwaitingCustomerSelection = isVelocitiVendorORUnifi && !selectedCustomer?.customerId;
  const customers: any[] = React.useMemo(() => {
    const stored = localStorage.getItem('customersList');
    return stored ? JSON.parse(stored) : [];
  }, []);
  console.log('selectedCustomer', selectedCustomer, 'isVelocitiVendorORUnifi', isVelocitiVendorORUnifi);

  const fetchJobs = useCallback(
    async (page: number, limit: number, searchValue: string = '') => {
      try {
        const fetchCustomerId = isVelocitiVendorORUnifi ? selectedCustomer?.customerId ?? '' : customerId;
        const fetchUserId = userId;

        if (!fetchCustomerId) return [];

        setIsJobsLoading(true);
        const sortBy = 'jobName';
        const sortOrderString = SortOrderType.ASC;
        const columnFilters = searchValue ? [{ id: sortBy, value: searchValue }] : [];

        const allJobResponse = await JobViewApiService.jobView(
          fetchCustomerId,
          fetchUserId,
          DEFAULT_FROM_DATE,
          JobType.Recurring,
          page,
          limit,
          sortBy,
          sortOrderString,
          columnFilters
        );
        setIsJobsLoading(false);

        if (allJobResponse?.data?.data?.length) {
          const jobsOptions = allJobResponse?.data?.data?.map(
            (job: { jobId: string; jobName: string; facilityId: string }) => ({
              label: job.jobName,
              value: job.jobId,
              facilityId: job.facilityId,
            })
          );
          setJobsOptions(jobsOptions);
          setTotalCount(allJobResponse?.data?.metadata?.totalCount);
        } else {
          setJobsOptions([]);
          setTotalCount(0);
        }
        return [];
      } catch (e) {
        console.error('Error', e);
        return [];
      }
    },
    [customerId, userId, isVelocitiVendorORUnifi, selectedCustomer?.customerId]
  );

  const handleSearch = useCallback(
    (value: string) => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = setTimeout(() => {
        setCurrentPage(DEFAULT_PAGE_NUMBER);
        fetchJobs(DEFAULT_PAGE_NUMBER, NUMBER_OF_ITEMS_PER_PAGE, value);
      }, DebounceTime);
    },
    [fetchJobs]
  );

  const handlePaginationChange = (event: any, value: number) => {
    setCurrentPage(value);
    fetchJobs(value, NUMBER_OF_ITEMS_PER_PAGE, searchString);
  };

  const handleJobSelection = (event: { target: { value: any } }) => {
    const value = event.target.value;

    const selected = jobsOptions?.find((job) => job?.value === value);

    setSelectedJob(value);
    setSelectedFacilityId(selected?.facilityId || '');
  };

  const handleChangeLocationDescription = (locationDescription: string) => {
    setLocationDescription(locationDescription);
  };

  const handleChangeIssueDescription = (issueDescription: string) => {
    setIssueDescription(issueDescription);
  };

  const handleChangeAdditionalNotes = (additionalNotes: string) => {
    setAdditionalNotes(additionalNotes);
  };

  const handleCustomerChange = (value: string) => {
    const selected = customers.find((c: any) => c.customerId === value);
    setSelectedCustomer(selected ?? null);
    setSelectedJob('');
    setJobsOptions([]);
    // Services are scoped to the customer, so drop whatever was picked for the previous one.
    setService(getDefaultServiceValueForScheduleCreation());
    setServiceTasks({});
  };

  const showSnackbar = (severity: AlertColor, message: string) => {
    setSnackbarOpen(true);
    setSnackbarSeverity(severity);
    setSnackbarMessage(message);
  };

  const handleCreateServiceRequest = async () => {
    try {
      setIsCreationLoading(true);

      const { customerId: storedCustomerId = '', userId: storedUserId = '' } = getStoredCustomerDetails() || {};
      const effectiveCustomerId = isVelocitiVendorORUnifi ? selectedCustomer?.customerId ?? '' : storedCustomerId;
      const effectiveUserId = storedUserId;
      const effectiveOrganizationId = isVelocitiVendorORUnifi ? storedCustomerId : effectiveCustomerId;
      const payload: ICreateServiceRequest = {
        orgId: effectiveOrganizationId,
        facilityId: selectedFacilityId,
        customerId: effectiveCustomerId,
        jobId: selectedJob,
        userId: effectiveUserId,
        mdServiceCategoryId: service?.[0]?.mdServiceId || '',
        locationDetails: locationDescription || '',
        description: issueDescription || '',
        attachments: jobAttachments?.map((file: any) => ({
          fileName: file.fileName,
          extension: file.fileExtension,
          fileURL: file.url,
        })),
        comments: additionalNotes
          ? [
              {
                userId: effectiveUserId,
                comment: additionalNotes,
              },
            ]
          : [],
      };

      const response = await JobCreateApiService.createServiceRequest(payload, effectiveUserId);

      if (response?.errors?.length) {
        showSnackbar(SnackbarSeverity.error, t('serviceRequests:errorMessage'));
      } else {
        showSnackbar(SnackbarSeverity.success, t('serviceRequests:successMessage'));
        setTimeout(() => {
          navigate(Paths.Home);
        }, timeoutValue);
      }
    } catch (error) {
      console.error('Create service request error:', error);
    } finally {
      setIsCreationLoading(false);
    }
  };

  const isFormValid =
    !!selectedJob &&
    !!service?.[0]?.mdServiceId &&
    !!issueDescription?.trim() &&
    (!isVelocitiVendorORUnifi || !!selectedCustomer?.customerId);

  const renderBottomBarContainer = () => {
    return (
      <BottomBarContainer>
        <Button
          disabled={isCreationLoading}
          sx={{ ...saveExitButton, opacity: isCreationLoading ? '0.5' : '1', cursor: 'pointer' }}
          onClick={() => navigate(Paths.Home)}>
          <Typography sx={{ ...buttonText, color: theme.palette.primary.dark }}>
            {t('serviceRequests:cancel')}
          </Typography>
        </Button>
        <Button
          disabled={isCreationLoading || !isFormValid}
          sx={{
            ...createJobButton,
            opacity: isCreationLoading || !isFormValid ? '0.5' : '1',
            maxWidth: '18rem',
          }}
          onClick={() => handleCreateServiceRequest()}>
          {isCreationLoading ? (
            <CircularProgress size={30} sx={{ color: theme.palette.common.white }} />
          ) : (
            <Typography sx={{ ...buttonText, color: theme.palette.common.white }}>
              {t('serviceRequests:serviceRequest')}
            </Typography>
          )}
        </Button>
      </BottomBarContainer>
    );
  };

  const handleAttachmentDetails = (data: any) => {
    localStorage.setItem(AsyncStorageKeys.serviceRequestAttachments, JSON.stringify(data));
    setJobAttachments(data);
    window.requestAnimationFrame(() => {
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: 'smooth',
      });
    });
  };

  const handleDeleteAttachment = (index: number) => {
    const item = jobAttachments[index];
    const filteredDetails = jobAttachments.filter((data) => data.id !== item.id);
    localStorage.setItem(AsyncStorageKeys.serviceRequestAttachments, JSON.stringify(filteredDetails));
    setJobAttachments(filteredDetails);
  };

  useEffect(() => {
    const fetchMultiCustomerOrgIds = async () => {
      setMultiCustomerOrgIds(await MasterDataApiService.getMultiCustomerOrgIds());
    };
    fetchMultiCustomerOrgIds();
  }, []);

  useEffect(() => {
    if (multiCustomerOrgIds && !isVelocitiVendorORUnifi) {
      fetchJobs(DEFAULT_PAGE_NUMBER, NUMBER_OF_ITEMS_PER_PAGE);
    }
  }, [multiCustomerOrgIds]);

  useEffect(() => {
    if (isVelocitiVendorORUnifi && selectedCustomer?.customerId) {
      fetchJobs(DEFAULT_PAGE_NUMBER, NUMBER_OF_ITEMS_PER_PAGE);
    }
  }, [selectedCustomer?.customerId]);

  return (
    <>
      <PageTitle title={t('serviceRequests:serviceRequest')} />
      <CustomerContainer>
        <Typography sx={headingText}>{t('serviceRequests:serviceRequestDetails')}</Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
          }}>
          {isVelocitiVendorORUnifi && (
            <Box>
              <TextField
                select
                sx={{ ...textField, marginBottom: '2rem' }}
                label={
                  <span>
                    {t('oneTimeJob:customer')} <span className="mandatory-field">*</span>
                  </span>
                }
                value={selectedCustomer?.customerId ?? ''}
                onChange={(event) => handleCustomerChange(event.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setCustomerOpen(!isCustomerOpen)} style={{ padding: 0 }}>
                        <img
                          src={isCustomerOpen ? Icons.DropdownDownIcon : Icons.DropdownUpIcon}
                          alt={isCustomerOpen ? 'down' : 'up'}
                        />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                SelectProps={{
                  open: isCustomerOpen,
                  IconComponent: () => null,
                  onOpen: () => setCustomerOpen(true),
                  onClose: () => setCustomerOpen(false),
                  MenuProps: {
                    PaperProps: {
                      style: {
                        boxShadow: BoxShadow.BoxShadow,
                      },
                    },
                  },
                }}>
                {customers?.map((customer: any, index: number) => (
                  <Options key={index} value={customer?.customerId}>
                    {customer?.legalName}
                  </Options>
                ))}
              </TextField>
            </Box>
          )}
          <Box sx={{ marginRight: '1.5rem', marginBottom: '2.5rem' }}>
            <CustomDropDownAutoComplete
              title={t('serviceRequests:location')}
              options={jobsOptions}
              selectedValue={selectedJob}
              onChange={handleJobSelection}
              isLoading={isJobsLoading}
              totalCounts={totalCount}
              handlePaginationChanges={handlePaginationChange}
              currentPages={currentPage}
              searchString={searchString}
              setSearchString={setSearchString}
              onSearch={handleSearch}
              dropDownHeight={1000}
              isInline={true}
              label={t('serviceRequests:jobName')}
              noOptionsText={isAwaitingCustomerSelection ? t('serviceRequests:selectCustomerFirst') : undefined}
            />
          </Box>
          <Box>
            <ScheduleService
              service={service}
              setService={setService}
              serviceTasks={serviceTasks}
              setServiceTasks={setServiceTasks}
              customerId={isVelocitiVendorORUnifi ? selectedCustomer?.customerId ?? '' : customerId}
              isServiceRequest={true}
            />
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
          }}>
          <Box>
            <JobNameContainer>
              <TextField
                sx={textField}
                id="outlined-required"
                label={
                  <span>
                    {t('serviceRequests:issueDescription')}
                    <span className="mandatory-field"> *</span>
                  </span>
                }
                value={issueDescription}
                onChange={(event) => {
                  const value = event?.target?.value;

                  handleChangeIssueDescription(value);
                }}
              />
            </JobNameContainer>
          </Box>
          <Box>
            <JobNameContainer>
              <TextField
                sx={textField}
                id="outlined-required"
                label={t('serviceRequests:locationDescription')}
                value={locationDescription}
                onChange={(event) => {
                  const value = event?.target?.value;
                  handleChangeLocationDescription(value);
                }}
              />
            </JobNameContainer>
          </Box>
          <Box>
            <JobNameContainer>
              <TextField
                sx={textField}
                id="outlined-required"
                label={t('serviceRequests:additionalNotes')}
                value={additionalNotes}
                onChange={(event) => {
                  const value = event?.target?.value;

                  handleChangeAdditionalNotes(value);
                }}
              />
            </JobNameContainer>
          </Box>
        </Box>
        <Divider
          sx={{
            ...divider,
          }}
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
          }}>
          <PhotosAndVideos
            customerId={customerId}
            jobId={selectedJob}
            details={jobAttachments}
            handleAttachmentDetails={handleAttachmentDetails}
            handleDeleteAttachment={handleDeleteAttachment}
          />
        </Box>
        {renderBottomBarContainer()}
      </CustomerContainer>
      <SnackbarMessage
        open={snackbarOpen}
        successMessage={snackbarMessage}
        errorMessage={snackbarMessage}
        severity={snackbarSeverity}
        onClose={() => setSnackbarOpen(false)}
      />
    </>
  );
};

export default ServiceRequest;
